import { useQuery } from '@tanstack/react-query';
import { useEffect, type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { FieldList, MetaBox } from '@/components/admin/MetaBox';
import { ErrorState, ForbiddenState, SkeletonRows } from '@/components/admin/states';
import {
  ATTENDANCE_PART_LABELS,
  ATTENDANCE_SOURCE_LABELS,
  ATTENDANCE_STATUS_LABELS,
  type AttendancePart,
  type AttendanceStatus,
} from '@/features/events/attendanceApi';
import { groupSurveysByKind } from '@/features/events/eventSurveyTabs';
import {
  fetchMemberEventResult,
  type MemberEventResult,
  type MemberEventSurvey,
} from '@/features/members/api';
import { MemberRelatedNav } from '@/features/members/MemberRelatedNav';
import { ApiError } from '@/lib/api/http';
import { formatDateTime as formatDateTimeValue } from '@/lib/datetime';

const ABSENCE_STATUS_LABELS: Record<string, string> = {
  pending: '受付中',
  approved: '承認済み',
  rejected: '差戻し',
};

const RESPONSE_STATUS_LABELS: Record<string, string> = {
  not_started: '未着手',
  draft: '下書き',
  submitted: '提出済み',
};

const ATTITUDE_LABELS: Record<string, string> = {
  good: '良い',
  bad: '気になる',
};

function label(dict: Record<string, string>, key: string): string {
  // 辞書に無い値は隠さずそのまま出す（D-083）
  return dict[key] ?? key;
}

function formatDateTime(value: string | null): string {
  return formatDateTimeValue(value) || '—';
}

/** 出欠（本編・交流会）。「誰が決めたか」まで出す（D-083の出所表示と同じ） */
function AttendanceSection({ result }: { result: MemberEventResult }): ReactElement {
  if (result.attendance.length === 0) {
    return <p className="text-wptext-muted">出欠の記録はありません。</p>;
  }
  return (
    <FieldList
      fields={result.attendance.map((row) => ({
        label: label(ATTENDANCE_PART_LABELS, row.part as AttendancePart),
        value: (
          <>
            <span className="font-semibold text-wptext-strong">
              {row.waitlisted
                ? 'キャンセル待ち'
                : label(ATTENDANCE_STATUS_LABELS, row.status as AttendanceStatus)}
            </span>
            <span className="ml-2 text-wptext-muted">
              （{label(ATTENDANCE_SOURCE_LABELS, row.source)}
              {row.decided_at ? ` / ${formatDateTime(row.decided_at)}` : ''}）
            </span>
            {row.note && <p className="mt-1 whitespace-pre-wrap">{row.note}</p>}
          </>
        ),
      }))}
    />
  );
}

function SurveyCard({ survey }: { survey: MemberEventSurvey }): ReactElement {
  return (
    <article>
      <h4 className="border-b border-wpborder pb-1 font-semibold text-wptext-strong">
        {survey.survey_name}
        <span className="ml-2 font-normal text-wptext-muted">
          {label(RESPONSE_STATUS_LABELS, survey.status)}
          {survey.submitted_at ? ` / 提出 ${formatDateTime(survey.submitted_at)}` : ''}
          {survey.deadline_at ? ` / 期限 ${formatDateTime(survey.deadline_at)}` : ''}
        </span>
      </h4>
      {survey.answers.length === 0 ? (
        <p className="mt-2 text-wptext-muted">
          {survey.status === 'not_started' ? 'まだ回答していません。' : '回答はありません。'}
        </p>
      ) : (
        <dl className="mt-2 flex flex-col gap-2">
          {survey.answers.map((answer) => (
            <div key={answer.question_id}>
              <dt className="text-wptext-muted">{answer.label}</dt>
              <dd className="whitespace-pre-wrap text-wptext-strong">{answer.value || '—'}</dd>
            </div>
          ))}
        </dl>
      )}
    </article>
  );
}

/** 課題・アンケートは種別ごとの区分見出しで並べる（月例会は D-329 の4区分） */
function SurveySection({ result }: { result: MemberEventResult }): ReactElement {
  if (result.surveys.length === 0) {
    return <p className="text-wptext-muted">このイベントに課題・アンケートはありません。</p>;
  }
  const groups = groupSurveysByKind(result.event_type_code, result.surveys);
  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => (
        <section key={group.kind} id={`survey-${group.kind}`} className="scroll-mt-4">
          <h3 className="mb-3 border-b border-wpborder pb-1 font-semibold text-wptext-strong">
            {group.label}
          </h3>
          <div className="flex flex-col gap-4">
            {group.items.map((survey) => (
              <SurveyCard key={survey.event_survey_id} survey={survey} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * 参加イベント1件の結果（D-150）。
 * ユーザー指摘「参加イベントクリックしたら、イベント情報が表示されるのではなく、
 * 受講生個々に対するイベントの案件やアンケート結果や出欠などの結果情報を一画面で
 * 確認出来るように」への対応。イベントの案内ではなく、**この受講生がどうだったか**を
 * セクションで並べる（D-149 と同じくタブは使わない）。
 * 権限で見えない情報はAPI側で空になる（席次・分科会FB・Meetログ / §0-6）。
 */
export function MemberEventResultPage(): ReactElement {
  const { memberId = '', eventId = '' } = useParams();
  const query = useQuery({
    queryKey: ['member-event-result', memberId, eventId],
    queryFn: () => fetchMemberEventResult(memberId, eventId),
    enabled: Boolean(memberId && eventId),
  });

  // 対象者一覧などから #survey-assignment 等で飛んできたとき該当区分へスクロール（D-341）
  useEffect(() => {
    if (!query.data) return;
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash.startsWith('survey-')) return;
    const el = document.getElementById(hash);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [query.data?.event_id]);

  if (query.isPending) return <SkeletonRows rows={6} />;
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 403) return <ForbiddenState />;
    return <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  const result = query.data;
  const hasSeat = result.table_no !== null;
  const showAttendance = result.attendance_required;

  return (
    <>
      <PageHeader
        title={`${result.member_name} さん — ${result.event_title}`}
        // 種別名（月例会 等）がイベント名と紛らわしいので、どの項目かを明示する（D-330）
        // 面談は日時未定で作れる（D-173）
        description={[
          `種別: ${result.event_type_name}`,
          `開催日時: ${result.starts_at ? formatDateTime(result.starts_at) : '日程調整中'}`,
          ...(result.venue ? [`会場: ${result.venue}`] : []),
        ].join(' ・ ')}
        actions={
          <Link to={`/events/${result.event_id}`} className="btn-secondary">
            イベントの詳細を見る
          </Link>
        }
      />
      <MemberRelatedNav memberId={result.member_id} current="event-result" />

      <div className="flex flex-col gap-4">
        {showAttendance && (
          <MetaBox title="出欠">
            <AttendanceSection result={result} />
          </MetaBox>
        )}

        {showAttendance && result.absence_request && (
          <MetaBox title="欠席申請">
            <FieldList
              fields={[
                {
                  label: '状態',
                  value: label(ABSENCE_STATUS_LABELS, result.absence_request.status),
                },
                { label: '理由', value: result.absence_request.reason },
                {
                  label: '判断',
                  value: result.absence_request.decided_at
                    ? `${formatDateTime(result.absence_request.decided_at)}（${
                        result.absence_request.decided_by_name ?? '—'
                      }）`
                    : '—',
                },
              ]}
            />
          </MetaBox>
        )}

        {hasSeat && (
          <MetaBox title="席次">
            <p className="text-wptext-muted">
              <span className="text-wptext-strong">{result.table_no}卓</span>
              {result.seat_no !== null && `${result.seat_no}番`}
            </p>
          </MetaBox>
        )}

        <MetaBox title="課題・アンケートの回答">
          <SurveySection result={result} />
        </MetaBox>

        {result.feedback && (
          <MetaBox title="分科会のフィードバック">
            <FieldList
              fields={[
                { label: '優秀者', value: result.feedback.is_best_performer ? 'はい' : 'いいえ' },
                {
                  label: '態度',
                  value: result.feedback.attitude
                    ? label(ATTITUDE_LABELS, result.feedback.attitude)
                    : '—',
                },
              ]}
            />
          </MetaBox>
        )}

        {result.meet_logs.length > 0 && (
          <MetaBox title="Meetの参加ログ">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-wpborder bg-wpcanvas/60">
                    <th className="px-2 py-1.5">入室</th>
                    <th className="px-2 py-1.5">退室</th>
                    <th className="px-2 py-1.5">滞在</th>
                    <th className="px-2 py-1.5">メールアドレス</th>
                  </tr>
                </thead>
                <tbody>
                  {result.meet_logs.map((log) => (
                    <tr key={`${log.email}-${log.join_at}`} className="border-b border-wpborder">
                      <td className="px-2 py-1.5">{formatDateTime(log.join_at)}</td>
                      <td className="px-2 py-1.5">{formatDateTime(log.leave_at)}</td>
                      <td className="px-2 py-1.5">
                        {log.duration_seconds !== null
                          ? `${Math.round(log.duration_seconds / 60)}分`
                          : '—'}
                      </td>
                      <td className="px-2 py-1.5">{log.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </MetaBox>
        )}
      </div>

      <Link
        to={`/members/${result.member_id}/events`}
        className="mt-4 inline-block text-wpaccent hover:underline"
      >
        ← 参加イベント一覧に戻る
      </Link>
    </>
  );
}
